import { useState } from "react";

// The Root Agent's video answers: the widget's own copy of the PIMS assistant
// page's cards (src/pages/Menu/TgPimsAssistant/components/VideoCards.js), so
// the embeddable widget only imports from its own folder.

// Leads with the video the AI picked and offers the rest as thumbnails.
//
// The lead is whichever card carries `primary` — NOT the first in the array:
// the backend re-ranks with an LLM because vector similarity and "actually
// answers the question" are different things. When no card is primary, every
// card is shown at equal weight rather than guessing a lead.
export default function VideoCards({ videos, language }) {
  // Overrides the lead when someone picks a thumbnail. Null means "use primary".
  const [selectedId, setSelectedId] = useState(null);

  if (!videos?.length) return null;

  const primary = videos.find((v) => v.primary);
  const selected = selectedId
    ? videos.find((v) => v.video_id === selectedId)
    : null;
  const lead = selected ?? primary;

  // No AI pick and nobody clicked: show them flat rather than promoting one.
  if (!lead) {
    return (
      <div className="flex flex-col gap-3 mt-1">
        {videos.map((video, i) => (
          <VideoCard
            key={video.video_id ?? i}
            video={video}
            language={language}
          />
        ))}
      </div>
    );
  }

  const others = videos.filter((v) => v !== lead);

  return (
    <div className="flex flex-col gap-2 mt-1">
      <VideoCard key={lead.video_id} video={lead} language={language} />
      {others.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <div className="text-[11px] font-medium text-gray-500">
            {language === "zh" ? "其他相關影片" : "Other related videos"}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {others.map((video) => (
              <VideoMiniCard
                key={video.video_id}
                video={video}
                language={language}
                onSelect={() => setSelectedId(video.video_id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// One video: player, summary, and a chapter list that seeks the player. The
// chapter the search hit (`matched`) starts expanded, so the card opens on the
// part that answers the question.
function VideoCard({ video, language }) {
  const chapters = Array.isArray(video.chapters) ? video.chapters : [];
  const matchedIndex = Math.max(
    0,
    chapters.findIndex((c) => c.matched),
  );
  const [openChapter, setOpenChapter] = useState(matchedIndex);
  const [startAt, setStartAt] = useState(video.start_seconds ?? 0);
  // Remounts the iframe so a chapter click actually seeks; changing the src of a
  // live YouTube embed does not reliably reposition it.
  const [playerKey, setPlayerKey] = useState(0);

  const seekTo = (seconds) => {
    setStartAt(seconds ?? 0);
    setPlayerKey((k) => k + 1);
  };

  if (!video.video_id) return null;

  return (
    <div className="rounded-xl border border-black/10 bg-white/70 overflow-hidden">
      <div className="px-3 py-2 border-b border-black/10 text-sm font-semibold text-gray-800">
        {video.title}
      </div>

      <div className="p-3 flex flex-col gap-3">
        <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-lg bg-black">
          <iframe
            key={playerKey}
            className="absolute top-0 left-0 w-full h-full border-0"
            src={`https://www.youtube.com/embed/${video.video_id}?start=${Math.floor(startAt)}`}
            title={video.title || "YouTube video player"}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>

        <a
          href={video.url}
          target="_blank"
          rel="noreferrer"
          className="self-start text-xs font-medium text-orange-700 hover:underline"
        >
          {language === "zh" ? "在 YouTube 觀看" : "Watch on YouTube"}
        </a>

        {video.content_scope === "title_only" && <p className="text-xs text-gray-500">
          {language === "zh" ? "僅依標題搜尋，無逐字稿或章節可供內容查證。" : "Matched by title only; no transcript or chapters are available."}
        </p>}
        {video.summary && (
          <div>
            <div className="text-xs font-semibold text-gray-700 mb-1">
              {language === "zh" ? "影片摘要" : "Video Summary"}
            </div>
            <p className="text-xs leading-relaxed text-gray-600">
              {video.summary}
            </p>
          </div>
        )}

        {chapters.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-gray-700 mb-1">
              {language === "zh" ? "章節" : "Chapters"}
            </div>
            <ul className="flex flex-col gap-1">
              {chapters.map((chapter, i) => (
                <li
                  key={`${chapter.start_seconds}-${i}`}
                  className={`rounded-lg border ${
                    chapter.matched
                      ? "border-orange-500/60 bg-orange-50"
                      : "border-black/10"
                  }`}
                >
                  <div className="flex items-center gap-2 px-2 py-1.5">
                    <button
                      type="button"
                      onClick={() => seekTo(chapter.start_seconds)}
                      className="text-[11px] font-mono tabular-nums text-orange-700 hover:underline shrink-0"
                      title={
                        language === "zh" ? "從這裡播放" : "Play from here"
                      }
                    >
                      {formatTimestamp(chapter.start_seconds)}
                    </button>
                    <button
                      type="button"
                      onClick={() => setOpenChapter(openChapter === i ? -1 : i)}
                      className="flex-1 text-left text-xs text-gray-800 min-w-0"
                    >
                      <span className="truncate block">{chapter.title}</span>
                    </button>
                    {chapter.matched && (
                      <span className="shrink-0 text-[10px] font-medium text-orange-700">
                        {language === "zh" ? "命中段落" : "matched"}
                      </span>
                    )}
                  </div>

                  {openChapter === i && chapter.transcript && (
                    <div className="px-2 pb-2">
                      <p className="text-[11px] leading-relaxed text-gray-600 whitespace-pre-wrap">
                        {chapter.transcript}
                      </p>
                      {/* Required by the backend contract: this text is raw ASR
                          with known mishearings, so it must never be presented
                          as an official transcript. The note disappears once a
                          human has checked the text. */}
                      {!chapter.transcript_verified && (
                        <p className="mt-1 text-[10px] italic text-gray-400">
                          {language === "zh"
                            ? "⚠ 自動語音辨識，未經校對，可能有誤字"
                            : "⚠ Auto-transcribed, unverified — may contain errors"}
                        </p>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

// A compact row for the runner-up videos. Clicking promotes it into the player
// above rather than opening a second player, so only one video ever plays.
function VideoMiniCard({ video, language, onSelect }) {
  const matched = video.chapters?.find((c) => c.matched);
  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex gap-2 items-start text-left rounded-lg border border-black/10 bg-white/60 hover:border-orange-500/60 hover:bg-white transition-colors p-2 w-full"
    >
      {video.video_id && (
        <img
          src={`https://img.youtube.com/vi/${video.video_id}/mqdefault.jpg`}
          alt=""
          loading="lazy"
          className="w-20 h-[45px] object-cover rounded shrink-0 bg-black"
        />
      )}
      <span className="flex flex-col min-w-0 gap-0.5">
        <span className="text-xs font-medium text-gray-800 line-clamp-2">
          {video.title}
        </span>
        {matched && (
          <span className="text-[10px] text-gray-500 truncate">
            {formatTimestamp(matched.start_seconds)} · {matched.title}
          </span>
        )}
      </span>
    </button>
  );
}

function formatTimestamp(seconds) {
  const total = Math.max(0, Math.floor(seconds ?? 0));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

// The backend sends each video twice: once as a markdown bullet in the answer
// text, once as a structured card. When the cards render, drop the bullets that
// duplicate them — matching on the video id so only lines the cards actually
// cover are removed, and any other prose survives untouched.
export function stripVideoList(message) {
  const text = message.text ?? "";
  const videos = message.videos;
  if (!videos?.length) return text;

  const ids = videos.map((v) => v.video_id).filter(Boolean);
  if (ids.length === 0) return text;

  return text
    .split("\n")
    .filter((line) => {
      const isBullet = /^\s*[-*]\s/.test(line);
      return !(isBullet && ids.some((id) => line.includes(id)));
    })
    .join("\n")
    .trim();
}
